/* import __COLOCATED_TEMPLATE__ from './form.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { Button, formatMoney, MONEY_TOKENS } from '@repo/design-system-kit';
import { dropTask } from 'ember-concurrency';

import { ANNUAL_TURNOVER_OPTIONS } from 'register-partners-js/constants/business-finances';
import { isOlderThanOrEqualOneYear } from 'register-partners-js/utils/dates';

import styles from './form.module.css';

const KYB_BUSINESS_FINANCES_FIELDS = ['yearlyTransactionVolume', 'maxSinglePaymentAmount'];

export default class OrganizationFinancesForm extends Component {
  styles = styles;
  button = Button;

  @service intl;

  @tracked hasAnnualTurnoverError = false;
  @tracked hasMaxSinglePaymentAmountError = false;

  willDestroy() {
    super.willDestroy(...arguments);
    this.args.organization.set('didValidate', false);
  }

  get selectedAnnualTurnoverValue() {
    let code = this.args.organization.annualTurnover;

    if (code) {
      let name = this.annualTurnoverOptionsTranslations[code];
      return { code, name };
    }
    return null;
  }

  submitTask = dropTask(async organization => {
    let { model, validations } = await organization.validate({
      on: this.showAnnualTurnoverField
        ? [...KYB_BUSINESS_FINANCES_FIELDS, 'annualTurnover']
        : KYB_BUSINESS_FINANCES_FIELDS,
    });

    this.hasAnnualTurnoverError = Boolean(
      validations.errors.filter(error => error.attribute === 'annualTurnover').length
    );
    this.hasMaxSinglePaymentAmountError = Boolean(
      validations.errors.filter(error => error.attribute === 'maxSinglePaymentAmount').length
    );

    if (validations.isValid) {
      this.hasAnnualTurnoverError = false;
      this.hasMaxSinglePaymentAmountError = false;
      return this.args.submitOrganizationTask.perform(organization);
    }

    model.set('didValidate', true);
  });

  handleGoBackTask = dropTask(async () => {
    await this.args.goBack();
  });

  @action
  handleAnnualTurnoverUpdate(annualTurnoverObject) {
    this.args.organization.annualTurnover = annualTurnoverObject.code;
  }

  get showAnnualTurnoverField() {
    let { legalRegistrationDate } = this.args.organization;
    return legalRegistrationDate && isOlderThanOrEqualOneYear(legalRegistrationDate);
  }

  get annualTurnoverOptionsTranslations() {
    return {
      [ANNUAL_TURNOVER_OPTIONS[0]]: this.intl.t(
        'partner-register-kyb.business-finances.annual-turnover.option-1',
        {
          amount: formatMoney({
            amount: 100000,
            token: MONEY_TOKENS.EUR,
          }),
        }
      ),
      [ANNUAL_TURNOVER_OPTIONS[1]]: this.intl.t(
        'partner-register-kyb.business-finances.annual-turnover.option-2',
        {
          minAmount: formatMoney({
            amount: 100000,
            token: MONEY_TOKENS.EUR,
          }),
          maxAmount: formatMoney({
            amount: 1000000,
            token: MONEY_TOKENS.EUR,
          }),
        }
      ),
      [ANNUAL_TURNOVER_OPTIONS[2]]: this.intl.t(
        'partner-register-kyb.business-finances.annual-turnover.option-3',
        {
          minAmount: formatMoney({
            amount: 1000000,
            token: MONEY_TOKENS.EUR,
          }),
          maxAmount: formatMoney({
            amount: 30000000,
            token: MONEY_TOKENS.EUR,
          }),
        }
      ),
      [ANNUAL_TURNOVER_OPTIONS[3]]: this.intl.t(
        'partner-register-kyb.business-finances.annual-turnover.option-4',
        {
          minAmount: formatMoney({
            amount: 30000000,
            token: MONEY_TOKENS.EUR,
          }),
          maxAmount: formatMoney({
            amount: 100000000,
            token: MONEY_TOKENS.EUR,
          }),
        }
      ),
      [ANNUAL_TURNOVER_OPTIONS[4]]: this.intl.t(
        'partner-register-kyb.business-finances.annual-turnover.option-5'
      ),
    };
  }

  get annualTurnoverOptions() {
    return ANNUAL_TURNOVER_OPTIONS.map(code => ({
      code,
      name: this.annualTurnoverOptionsTranslations[code],
    }));
  }
}
